import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { contactInfo, localize } from '../mock';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import translations from '../translations';
import SEO from '../components/SEO';
import { SectionHero } from '../components/Sections';

const ContactInfoItem = ({ icon: Icon, label, children }) => (
  <div className="flex items-start space-x-4">
    <div className="bg-orange-100 p-3 rounded-lg flex-shrink-0">
      <Icon className="h-6 w-6 text-orange-500" />
    </div>
    <div>
      <h3 className="font-semibold text-gray-900 mb-1">{label}</h3>
      {children}
    </div>
  </div>
);

const ContactInfoPanel = ({ t, lang }) => (
  <div className="space-y-8">
    <div>
      <h2 className="text-3xl font-bold text-gray-900 mb-6">{t.getInTouch}</h2>
      <p className="text-gray-600 mb-8">{t.getInTouchDescription}</p>
    </div>
    <Card className="border-0 shadow-xl">
      <CardContent className="p-6 space-y-6">
        <ContactInfoItem icon={Mail} label={t.email}>
          <a href={`mailto:${contactInfo.email}`} className="text-gray-600 hover:text-orange-500 transition-colors">{contactInfo.email}</a>
        </ContactInfoItem>
        <ContactInfoItem icon={Phone} label={t.phone}>
          <a href={`tel:${contactInfo.phone}`} className="text-gray-600 hover:text-orange-500 transition-colors">{contactInfo.phone}</a>
        </ContactInfoItem>
        <ContactInfoItem icon={MapPin} label={t.address}>
          <p className="text-gray-600">{contactInfo.address}</p>
        </ContactInfoItem>
        <ContactInfoItem icon={Clock} label={t.businessHours}>
          <p className="text-gray-600">{localize(contactInfo, 'hours', lang)}</p>
        </ContactInfoItem>
      </CardContent>
    </Card>
  </div>
);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = ({ t, lang }) => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', service: '', message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Failed to submit');
      toast({ title: t.toastTitle, description: t.toastDescription });
      setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
    } catch {
      toast({ title: lang === 'es' ? 'Error' : 'Error', description: lang === 'es' ? 'No se pudo enviar el mensaje. Inténtelo de nuevo.' : 'Failed to send message. Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-0 shadow-xl">
      <CardContent className="p-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">{t.sendMessage}</h2>
        <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2">{t.fullName} *</label>
              <Input id="name" name="name" type="text" required data-testid="contact-name-input" value={formData.name} onChange={handleChange} placeholder="John Doe" className="w-full" />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">{t.emailAddress} *</label>
              <Input id="email" name="email" type="email" required data-testid="contact-email-input" value={formData.email} onChange={handleChange} placeholder="john@example.com" className="w-full" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-2">{t.phoneNumber}</label>
              <Input id="phone" name="phone" type="tel" data-testid="contact-phone-input" value={formData.phone} onChange={handleChange} placeholder="+1 (616) 405-0194" className="w-full" />
            </div>
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-900 mb-2">{t.companyName}</label>
              <Input id="company" name="company" type="text" data-testid="contact-company-input" value={formData.company} onChange={handleChange} placeholder={lang === 'es' ? 'Su Empresa' : 'Your Company'} className="w-full" />
            </div>
          </div>
          <div>
            <label htmlFor="service" className="block text-sm font-medium text-gray-900 mb-2">{t.serviceOfInterest}</label>
            <select id="service" name="service" data-testid="contact-service-select" value={formData.service} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500">
              <option value="">{t.selectService}</option>
              {t.serviceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2">{t.message} *</label>
            <Textarea id="message" name="message" required data-testid="contact-message-textarea" value={formData.message} onChange={handleChange} placeholder={t.messagePlaceholder} rows={6} className="w-full" />
          </div>
          <Button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-xl group disabled:opacity-50">
            {submitting ? (lang === 'es' ? 'Enviando...' : 'Sending...') : t.submitButton}
            <Send className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const Contact = () => {
  const { lang } = useLanguage();
  const t = translations[lang].contact;

  return (
    <div className="min-h-screen">
      <SEO path="/contact" />
      <SectionHero title1={t.heroTitle1} titleAccent={t.heroTitleAccent} title2="" description={t.heroDescription} />

      <section data-testid="contact-form-section" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <ContactInfoPanel t={t} lang={lang} />
            <div className="lg:col-span-2">
              <ContactForm t={t} lang={lang} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-200 rounded-2xl h-96 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">{t.mapPlaceholder}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
